<template>
  <button v-if="allowed" ref="trigger" type="button"
    class="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/15 bg-white/5 text-gray-300 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
    title="Ver historial" aria-label="Ver historial de cambios" @click="openHistory">
    <Eye :size="17" aria-hidden="true" />
  </button>
  <Teleport to="body">
    <div v-if="open && allowed" class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      @click.self="closeHistory">
      <section ref="dialog" role="dialog" aria-modal="true" :aria-labelledby="titleId" tabindex="-1"
        class="flex max-h-[calc(100dvh-2rem)] w-full max-w-[700px] flex-col overflow-hidden rounded-2xl border border-white/10 bg-bochas-bg shadow-2xl focus:outline-none [color-scheme:dark]">
        <header class="flex shrink-0 items-start justify-between gap-4 border-b border-white/10 px-5 py-4 sm:px-6">
          <div class="min-w-0">
            <h2 :id="titleId" class="text-xl font-semibold tracking-tight text-white">Historial de cambios</h2>
            <p class="mt-1 text-xs leading-5 text-gray-400 sm:text-sm">{{ record.title || record.name }}</p>
          </div>
          <button type="button" aria-label="Cerrar historial"
            class="-mr-1 -mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            @click="closeHistory"><X :size="20" aria-hidden="true" /></button>
        </header>
        <div role="region" aria-label="Contenido del historial" :tabindex="items.length ? 0 : undefined"
          class="min-h-0 overflow-y-auto focus:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-white/25">
          <div class="grid gap-6 px-5 py-5 sm:grid-cols-[220px_minmax(0,1fr)] sm:gap-6 sm:px-6">
            <aside class="border-b border-white/10 pb-6 sm:border-b-0 sm:border-r sm:pb-0 sm:pr-6" aria-label="Datos del registro">
              <h3 class="text-sm font-semibold leading-6 text-white">Datos del registro</h3>
              <dl class="mt-5 grid grid-cols-2 gap-x-4 gap-y-5 sm:grid-cols-1">
                <div>
                  <dt class="text-xs leading-5 text-gray-400">Creación</dt>
                  <dd class="mt-0.5 text-sm leading-5 text-white">{{ formatTimestamp(creation) }}</dd>
                </div>
                <div>
                  <dt class="text-xs leading-5 text-gray-400">Última actualización</dt>
                  <dd class="mt-0.5 text-sm leading-5 text-white">{{ formatTimestamp(lastUpdate) }}</dd>
                </div>
                <div class="col-span-2 sm:col-span-1">
                  <dt class="text-xs leading-5 text-gray-400">Autor registrado</dt>
                  <dd class="mt-0.5 break-words text-sm leading-5 text-white">{{ registeredAuthor || "—" }}</dd>
                </div>
              </dl>
            </aside>
            <section class="min-w-0" aria-label="Actividad del registro">
              <h3 class="text-sm font-semibold leading-6 text-white">Actividad</h3>
              <div v-if="loading && !items.length" class="flex min-h-[180px] items-center justify-center gap-2 text-sm text-gray-400" role="status">
                <LoaderCircle :size="18" class="animate-spin" aria-hidden="true" />
                Cargando historial…
              </div>
              <div v-if="error" class="mt-5 space-y-3 rounded-lg border border-red-400/15 bg-red-400/5 p-4" role="alert">
                <p class="text-sm leading-relaxed text-red-300">{{ error }}</p>
                <button type="button" class="rounded text-sm font-medium text-sky-200 underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                  @click="loadHistory(items.length > 0)">Reintentar</button>
              </div>
              <div v-if="!loading && !error && !items.length" class="flex min-h-[180px] flex-col items-center justify-center px-2 py-6 text-center">
                <History :size="40" :stroke-width="1.5" class="mb-5 text-gray-400" aria-hidden="true" />
                <h4 class="text-base font-semibold text-white">Aún no hay cambios</h4>
                <p class="mt-2 max-w-[270px] text-sm leading-relaxed text-gray-400">
                  Las próximas modificaciones aparecerán aquí con su fecha y autor.
                </p>
              </div>
              <ol v-if="items.length" class="mt-5 divide-y divide-white/10">
                <li v-for="entry in items" :key="entry.id" class="py-5 first:pt-0 last:pb-0">
                  <div class="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
                    <h4 class="text-sm font-semibold text-white">{{ sourceLabel(entry.source) }}{{ actionLabels[entry.action] || entry.action }}</h4>
                    <time :datetime="entry.created_at" class="text-xs leading-5 text-gray-400">{{ formatTimestamp(entry.created_at) }}</time>
                  </div>
                  <p class="mt-1 text-xs leading-5 text-gray-400">{{ entry.actor_name }}</p>
                  <dl class="mt-4 space-y-4">
                    <div v-for="change in entry.changes" :key="change.field" class="text-sm">
                      <dt class="mb-2 font-medium text-gray-300">{{ fieldLabels[change.field] || change.field.replace(/_/g, ' ') }}</dt>
                      <dd class="space-y-2">
                        <div v-if="entry.action !== 'create'" class="border-l-2 border-white/10 pl-3">
                          <p class="mb-1 text-xs text-gray-500">Antes</p>
                          <p class="max-h-32 overflow-auto whitespace-pre-wrap break-words text-xs leading-relaxed text-gray-400">{{ formatValue(change.before) }}</p>
                        </div>
                        <div v-if="entry.action !== 'delete'" class="border-l-2 border-sky-300/25 pl-3">
                          <p class="mb-1 text-xs text-gray-400">Después</p>
                          <p class="max-h-32 overflow-auto whitespace-pre-wrap break-words text-xs leading-relaxed text-gray-200">{{ formatValue(change.after) }}</p>
                        </div>
                      </dd>
                    </div>
                  </dl>
                </li>
              </ol>
              <button v-if="hasMore" type="button" :disabled="loading" class="btn-bochas-trans mt-5 w-full text-sm"
                @click="loadHistory(true)">{{ loading ? "Cargando…" : "Ver cambios anteriores" }}</button>
            </section>
          </div>
        </div>
        <footer class="flex shrink-0 items-center gap-2 border-t border-white/10 px-5 py-3 text-xs leading-5 text-gray-500 sm:px-6">
          <Info :size="15" class="shrink-0" aria-hidden="true" />
          <p>Historial disponible desde su activación.</p>
        </footer>
      </section>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, ref, nextTick, onBeforeUnmount } from "vue";
import { Eye, X, History, Info, LoaderCircle } from "lucide-vue-next";
import apiAuth from "@/services/http/apiAuth";
import { getStoredUser } from "@/services/auth/authService";
import { isGeneralAdmin } from "@/utils/roles";

const props = defineProps({
  record: { type: Object, required: true },
  resource: { type: String, required: true },
});
const allowed = computed(() => isGeneralAdmin(getStoredUser()));
const open = ref(false);
const dialog = ref(null);
const trigger = ref(null);
const loading = ref(false);
const error = ref("");
const items = ref([]);
const hasMore = ref(false);
const registeredAuthor = ref("");
const creation = ref(props.record.created_at ?? props.record.createdAt);
const lastUpdate = ref(props.record.updated_at ?? props.record.updatedAt);
const titleId = `record-history-${props.resource}-${props.record.id}`;
let requestVersion = 0;
let previousOverflow = "";
const formatter = new Intl.DateTimeFormat("es-AR", {
  day: "2-digit", month: "2-digit", year: "numeric",
  hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "America/Argentina/Cordoba",
});
const actionLabels = { create: "Creación", update: "Actualización", delete: "Eliminación" };
const fieldLabels = {
  name: "Nombre", title: "Título", description: "Descripción", content: "Contenido",
  contacts: "Contactos", categories: "Categorías", location: "Localidad",
  appointmentDay: "Fecha", appointmentTime: "Horario", event_date: "Fecha",
  category_date: "Fecha de categoría", category: "Categoría", status: "Estado",
  organizer: "Organizador", organizerClub: "Club organizador", association: "Asociación",
  asociacionId: "Asociación", clubId: "Club", provinceId: "Provincia", region: "Región",
  sorteoDate: "Fecha del sorteo", sorteoTime: "Horario del sorteo", address: "Dirección",
  additionalInfo: "Información adicional", locality: "Localidad", phone: "Teléfono",
  email: "Correo", foundationYear: "Año de fundación", president: "Presidente",
  courtType: "Tipo de cancha", logoUrl: "Logo", heroImageUrl: "Imagen principal", gallery: "Galería",
  playerCount: "Jugadores", clubCount: "Clubes", lat: "Latitud", lng: "Longitud",
  position: "Puesto", player_name: "Jugador", club: "Club", note: "Nota", city: "Localidad",
  display_order: "Orden", url: "Imagen", is_cover: "Portada", order: "Orden",
  tournament_id: "Torneo", category_id: "Categoría", news_id: "Publicación",
  published_at: "Fecha de publicación", type: "Tipo", tag_id: "Categoría de publicación",
  original_name: "Nombre de archivo", size: "Tamaño", mime_type: "Tipo de archivo",
  stored_name: "Archivo", uploaded_by: "Usuario que subió el archivo", entity_type: "Tipo de registro",
  teams_4_points: "Equipos de 4 puntos", teams_3_points: "Equipos de 3 puntos", teams_total: "Total de equipos",
};

function formatTimestamp(value) {
  if (!value) return "—";
  const date = new Date(typeof value === "string" ? value.replace(" ", "T") : value);
  return Number.isNaN(date.getTime()) ? "—" : formatter.format(date).replace(", ", " · ");
}

function formatValue(value) {
  if (value === null || value === undefined || value === "") return "Sin valor";
  if (typeof value === "boolean") return value ? "Sí" : "No";
  if (Array.isArray(value)) return value.map((item) =>
    typeof item === "object" && item !== null && ("name" in item || "phone" in item)
      ? [item.name, item.phone].filter(Boolean).join(" · ") : formatValue(item)).join("\n") || "Sin valor";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  const labels = { published: "Publicado", draft: "Borrador", cancelled: "Cancelado" };
  return labels[value] || String(value);
}

function sourceLabel(source) {
  return { tournament_category: "Categoría · ", podium: "Podio · ", tournament_date: "Fecha · ", news_image: "Imagen · ", files: "Archivo · " }[source] || "";
}

async function loadHistory(append = false) {
  if (!allowed.value || loading.value) return;
  const version = ++requestVersion;
  loading.value = true;
  error.value = "";
  try {
    const response = await apiAuth.get(`/admin/activity/records/${props.resource}/${props.record.id}`, {
      params: { offset: append ? items.value.length : 0 },
    });
    if (version !== requestVersion || !open.value) return;
    items.value = append ? [...items.value, ...response.data.items] : response.data.items;
    hasMore.value = response.data.hasMore;
    registeredAuthor.value = response.data.registeredAuthor || "";
    creation.value = response.data.createdAt;
    lastUpdate.value = response.data.updatedAt;
  } catch {
    if (version === requestVersion && open.value) error.value = "No se pudo cargar el historial. Intentá nuevamente.";
  } finally {
    if (version === requestVersion) loading.value = false;
  }
}

async function openHistory() {
  if (!allowed.value) return;
  open.value = true;
  items.value = [];
  hasMore.value = false;
  previousOverflow = document.body.style.overflow;
  document.body.style.overflow = "hidden";
  window.addEventListener("keydown", onKeydown);
  await nextTick();
  dialog.value?.focus();
  await loadHistory();
}

function closeHistory() {
  if (!open.value) return;
  open.value = false;
  requestVersion++;
  loading.value = false;
  document.body.style.overflow = previousOverflow;
  window.removeEventListener("keydown", onKeydown);
  trigger.value?.focus();
}

function onKeydown(event) {
  if (event.key === "Escape") { event.preventDefault(); closeHistory(); }
  if (event.key !== "Tab" || !dialog.value) return;
  const buttons = [...dialog.value.querySelectorAll("button:not([disabled]), a[href], [tabindex='0']")];
  const first = buttons[0], last = buttons[buttons.length - 1];
  if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.value)) {
    event.preventDefault(); last?.focus();
  } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog.value)) {
    event.preventDefault(); first?.focus();
  }
}
onBeforeUnmount(closeHistory);
</script>
